import type { ConfigurationFreshnessStatus, ConfigurationRevalidationStatus } from './api';

export function prefillStateLabel(state: 'DRAFT' | 'SUBMITTED'): string {
  return state === 'SUBMITTED' ? 'Отправлена' : 'Черновик';
}

export function freshnessLabel(freshness: ConfigurationFreshnessStatus): string {
  return freshness === 'CONFIGURATION_REFRESH_REQUIRED'
    ? 'Требования к осмотру обновились'
    : 'Актуальная конфигурация';
}

export function revalidationStatusLabel(status: ConfigurationRevalidationStatus): string {
  if (status === 'RESOLVED') {
    return 'Дозаполнение завершено';
  }
  if (status === 'OBSOLETE') {
    return 'Требование устарело';
  }
  return 'Требуется дозаполнение';
}

export function bookingStatusLabel(status: string): string {
  const normalized = status.trim().toUpperCase();
  if (normalized === 'BOOKED') {
    return 'Подтверждена';
  }
  if (normalized === 'CANCELLED') {
    return 'Отменена';
  }
  if (normalized === 'NO_SHOW') {
    return 'Неявка';
  }
  return status;
}

export function questionnaireStatusLabel(state: 'DRAFT' | 'SUBMITTED'): string {
  return state === 'SUBMITTED' ? 'Отправлена' : 'Черновик';
}

export function formatContractorDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('ru-RU');
}

/** Contractor dropdowns: business display name only (IDs/codes stay in option values). */
export function optionBusinessLabel(_code: string, displayName: string): string {
  const name = displayName.trim();
  return name.length > 0 ? name : 'Без названия';
}

const SUBJECT_BINDING_LABELS: Record<string, string> = {
  VEHICLE: 'ТС',
  DRIVER: 'Водитель',
  EQUIPMENT: 'Оборудование',
  ORGANIZATION: 'Организация',
  PERSON_OPERATOR: 'Оператор',
  ORGANIZATION_EQUIPMENT: 'Организация / оборудование',
};

function subjectBindingLabel(binding: string): string {
  return SUBJECT_BINDING_LABELS[binding] ?? 'субъект';
}

type DiffLabelSchema = {
  readonly fields?: readonly {
    readonly field_key: string;
    readonly label: string;
    readonly semantic_key?: string | null;
    readonly snapshot_path?: string | null;
  }[];
  readonly document_requirements?: readonly {
    readonly document_type_id: string;
    readonly subject_binding: string;
    readonly display_label: string;
    readonly metadata_fields?: readonly {
      readonly field_code: string;
      readonly label: string;
    }[];
  }[];
};

/**
 * Map rebase/diff identity tokens to business labels when schema is available.
 * Never returns raw CASE_* / UUID::BINDING to the contractor.
 */
export function formatConfigurationDiffIdentity(
  identity: string,
  schema: DiffLabelSchema | null | undefined,
): string {
  const trimmed = identity.trim();
  if (trimmed.length === 0) {
    return 'Элемент конфигурации';
  }

  const caseDynamic = trimmed.match(/^CASE_DYNAMIC:(.+)$/);
  if (caseDynamic !== null) {
    const key = caseDynamic[1]!;
    const field = schema?.fields?.find(
      (row) => row.semantic_key === key || row.field_key === key,
    );
    return field?.label?.trim() || 'Поле анкеты';
  }

  const caseSnapshot = trimmed.match(/^CASE_SNAPSHOT:(.+)$/);
  if (caseSnapshot !== null) {
    const path = caseSnapshot[1]!;
    const field = schema?.fields?.find(
      (row) => row.snapshot_path === path || row.field_key === path,
    );
    return field?.label?.trim() || 'Поле анкеты';
  }

  const docMatch = trimmed.match(/^([^:]+)::([A-Z_]+)$/);
  if (docMatch !== null) {
    const documentTypeId = docMatch[1]!;
    const binding = docMatch[2]!;
    const docs = schema?.document_requirements ?? [];
    const doc = docs.find(
      (row) => row.document_type_id === documentTypeId && row.subject_binding === binding,
    );
    if (doc?.display_label?.trim()) {
      return `${doc.display_label.trim()} (${subjectBindingLabel(binding)})`;
    }
    return `Документ (${subjectBindingLabel(binding)})`;
  }

  const byKey = schema?.fields?.find((row) => row.field_key === trimmed);
  if (byKey?.label?.trim()) {
    return byKey.label.trim();
  }
  if (/^[0-9a-f-]{36}/i.test(trimmed) || trimmed.includes('CASE_') || trimmed.includes('::')) {
    return 'Элемент конфигурации';
  }
  return trimmed;
}

/** Russian contractor-facing validation copy; keeps server codes authoritative. */
export function formatContractorValidationMessage(input: {
  readonly field: string;
  readonly code: string;
  readonly message?: string | null;
  readonly schema?: DiffLabelSchema | null;
}): string {
  const field = input.field;
  const code = input.code;
  const schema = input.schema;

  if (field === 'vehicle.vin_or_alternative_identifier' && code === 'REQUIRED') {
    return 'Заполните поле «VIN / идентификатор ТС».';
  }
  if (field === 'vehicle.registration_number' && code === 'REQUIRED') {
    return 'Заполните поле «Государственный регистрационный знак».';
  }

  const schemaField = schema?.fields?.find(
    (row) =>
      row.field_key === field ||
      row.semantic_key === field ||
      row.snapshot_path === field,
  );
  if (schemaField?.label && code === 'REQUIRED') {
    return `Заполните поле «${schemaField.label}».`;
  }

  if (field.includes('::') && code === 'REQUIRED') {
    const docKey = field.split('.')[0] ?? field;
    const [documentTypeId, binding] = docKey.split('::');
    const doc = schema?.document_requirements?.find(
      (row) =>
        row.document_type_id === documentTypeId &&
        (binding === undefined || row.subject_binding === binding),
    );
    const metaCode = field.includes('.') ? field.split('.').slice(1).join('.') : null;
    const meta = doc?.metadata_fields?.find((row) => row.field_code === metaCode);
    if (meta?.label) {
      return `Заполните реквизит «${meta.label}» документа «${doc?.display_label ?? 'Документ'}».`;
    }
    if (doc?.display_label) {
      return `Заполните документ «${doc.display_label}».`;
    }
  }

  const raw = input.message?.trim() ?? '';
  if (raw.length === 0) {
    return code === 'REQUIRED' ? 'Заполните обязательное поле.' : 'Проверьте данные анкеты.';
  }
  if (/Vehicle VIN or alternative identifier is required/i.test(raw)) {
    return 'Заполните поле «VIN / идентификатор ТС».';
  }
  if (/^[A-Za-z][A-Za-z0-9 _./:-]*$/.test(raw) && !/[А-Яа-яЁё]/.test(raw)) {
    if (code === 'REQUIRED') {
      return 'Заполните обязательное поле.';
    }
    return 'Проверьте данные анкеты.';
  }
  return raw;
}

export function isInitialEmptyConfigurationPreview(
  currentFingerprint: string | null,
  preview: {
    readonly preserved_value_count: number;
    readonly incompatible_value_count: number;
  },
): boolean {
  return (
    currentFingerprint === null &&
    preview.preserved_value_count === 0 &&
    preview.incompatible_value_count === 0
  );
}

export function shouldShowReuseSection(
  reuse:
    | {
        readonly resolution_state: string;
        readonly contractor_options: readonly unknown[];
        readonly vehicle_options: readonly unknown[];
      }
    | null
    | undefined,
): boolean {
  if (reuse === undefined || reuse === null) {
    return false;
  }
  if (reuse.resolution_state === 'REVIEW_REQUIRED') {
    return true;
  }
  return reuse.contractor_options.length > 0 || reuse.vehicle_options.length > 0;
}
